// Gammes jouables sur la progression en cours, ancrees sur le centre tonal.
//
// Pendant qu'une progression tourne, la question du guitariste n'est pas "quelle
// gamme genere ces accords" mais "quelle gamme je peux poser dessus". L'app
// etant construite autour d'un drone, toutes les suggestions partent de la meme
// fondamentale : sur une pedale de A, "C Major" n'existe pas, il n'y a que du
// A Aeolian. Proposer les relatives reviendrait a lister sept noms pour un seul
// ensemble de notes, et a inviter l'oreille a changer de maison. Deux familles
// repondent, et les deux sont utiles :
//
//  - couvrantes : la gamme contient toutes les notes des accords joues. On peut
//    la parcourir d'un bout a l'autre de la progression sans jamais tomber a
//    cote (A Harmonic Minor sur un i-III-IVsus en A Hirajoshi).
//  - internes : toutes les notes de la gamme appartiennent a la tonalite, meme
//    si la gamme ne couvre pas tous les accords. C'est ce qui fait entrer les
//    pentatoniques (C major pentatonic sur du C majeur) : elles n'ont aucune
//    note etrangere, elles en ont juste moins.
//
// Les gammes blues restent dehors : leur blue note est hors tonalite, donc ni
// couvrante ni interne. C'est assume, la regle ne juge pas le gout.
//
// Couvrir ne suffit pas : une gamme peut contenir toutes les notes des accords
// et poser ses notes a elle un demi-ton AU-DESSUS de ce qui sonne (la neuvieme
// mineure, le frottement le plus dur). A Ultraphrygian couvre un i-III de
// A Hirajoshi tout en ajoutant Bb sur le A, C# sur le C et F# sur le F : valide
// sur le papier, injouable a l'oreille. Chaque note hors tonalite un demi-ton
// au-dessus d'une note jouee compte un point de friction : deux points excluent
// la gamme, un point la marque harsh et la retrograde. Le demi-ton EN DESSOUS
// (la sensible, le D# de A Lydian sous le E) ne compte pas : il resout, il ne
// frotte pas.
//
// Le calcul ne touche jamais l'harmonie : il repond en gammes d'affichage, la
// progression continue de sonner sur sa gamme d'origine.

import {
  MODES,
  MODE_TO_TIER,
  ALL_NOTES,
  RANDOM_PROGRESSION,
  resolveAllNotesIndex,
  scaleIndexSet,
  getNotesInMode,
  getChordDescriptorsInMode,
  getDisplayRootNote,
  getModeDisplayLabel,
} from './musicTheory';
import { getProgressionByName, degreeToIndex } from './chordProgressions';
import formatAccidentals from './formatAccidentals';

const MAX_SUGGESTIONS = 14;
const INSIDE_SEATS = 5; // places gardees aux gammes internes (pentatoniques en tete)
const FRICTION_LIMIT = 2; // deux frottements en demi-ton superieur = gamme ecartee

// Les tiers de l'app rangent deja les gammes du courant a l'exotique. Sans ce
// classement, une poignee de gammes japonaises passe devant les pentatoniques
// dans du do majeur : elles sont internes a la tonalite, donc valides, mais ce
// n'est pas ce qu'un guitariste cherche en premier.
const TIER_RANK = { essentials: 0, colors: 1, wild: 2 };
const tierRank = (mode) => {
  const rank = TIER_RANK[MODE_TO_TIER[mode]];
  return rank === undefined ? 3 : rank;
};

// Hauteurs reellement jouees par la progression, accords compris (pas seulement
// leurs fondamentales). Sans progression exploitable (AUTO tire ses accords de
// la gamme entiere), la tonalite entiere fait office de reference.
export const getProgressionPitchClasses = (rootNote, mode, progressionName, chordExtension = '3') => {
  const scaleNotes = getNotesInMode(rootNote, mode);
  if (!scaleNotes.length) return new Set();

  const progression = progressionName && progressionName !== RANDOM_PROGRESSION
    ? getProgressionByName(progressionName)
    : null;

  if (!progression || !Array.isArray(progression.degrees) || !progression.degrees.length) {
    return scaleIndexSet(scaleNotes);
  }

  const descriptors = getChordDescriptorsInMode(rootNote, mode, chordExtension);
  const pcs = new Set();
  for (const degree of progression.degrees) {
    const chord = descriptors[degreeToIndex(degree, scaleNotes.length)];
    if (!chord) continue;
    for (const note of chord.notes || [chord.note]) {
      pcs.add(resolveAllNotesIndex(note));
    }
  }
  return pcs.size ? pcs : scaleIndexSet(scaleNotes);
};

/**
 * Gammes posables sur la progression en cours, toutes ancrees sur le centre
 * tonal, gamme harmonique exclue (elle reste le point de retour, affiche a
 * part). Une liste vide est un resultat honnete : aucune autre couleur ne
 * tient sur cette progression depuis cette fondamentale.
 * @param {object} params
 * @param {string} params.rootNote - fondamentale de la gamme harmonique
 * @param {string} params.mode - mode harmonique, celui qui genere les accords
 * @param {string} params.progressionName - progression en cours (null ou AUTO accepte)
 * @param {string} params.chordExtension - '1' | '3' | '7', pour les notes d'accord
 * @param {function} [params.isModeUnlocked] - filtre d'acces sur le mode
 * @param {function} [params.isRootNoteUnlocked] - filtre d'acces sur la tonalite
 * @returns {Array<{root: string, mode: string, label: string, covers: boolean, harsh: boolean}>}
 */
export const getCompatibleImproScales = ({
  rootNote,
  mode,
  progressionName,
  chordExtension = '3',
  isModeUnlocked,
  isRootNoteUnlocked,
}) => {
  const scaleNotes = getNotesInMode(rootNote, mode);
  if (!scaleNotes.length) return [];

  const played = getProgressionPitchClasses(rootNote, mode, progressionName, chordExtension);
  const tonality = scaleIndexSet(scaleNotes);
  const rootIdx = resolveAllNotesIndex(rootNote);

  const root = ALL_NOTES[rootIdx];
  if (isRootNoteUnlocked && !isRootNoteUnlocked(root)) return [];

  const found = [];
  for (const [modeName, data] of Object.entries(MODES)) {
    if (modeName === mode) continue;
    if (isModeUnlocked && !isModeUnlocked(modeName)) continue;
    const intervals = (data && data.intervals) || data;
    if (!Array.isArray(intervals) || intervals.length < 5) continue;

    let mask = 0;
    const notes = new Set();
    for (const iv of intervals) {
      const pc = (rootIdx + iv) % 12;
      notes.add(pc);
      mask |= 1 << pc;
    }

    let covers = true;
    for (const pc of played) {
      if (!notes.has(pc)) { covers = false; break; }
    }
    let inside = true;
    for (const pc of notes) {
      if (!tonality.has(pc)) { inside = false; break; }
    }
    if (!covers && !inside) continue;

    // Friction : notes hors tonalite posees un demi-ton au-dessus d'une note
    // jouee par la progression. Les notes de la tonalite ne comptent pas, elles
    // sont la couleur assumee de la gamme harmonique (le b2 d'un Phrygian).
    let friction = 0;
    for (const pc of notes) {
      if (tonality.has(pc)) continue;
      if (played.has((pc + 11) % 12)) friction++;
    }
    if (friction >= FRICTION_LIMIT) continue;

    // Notes d'accord effectivement dans la gamme : departage deux gammes du
    // meme rang, celle qui touche le plus d'accords passe devant.
    let reached = 0;
    for (const pc of played) {
      if (notes.has(pc)) reached++;
    }
    found.push({
      root,
      mode: modeName,
      covers,
      harsh: friction > 0,
      reached,
      tier: tierRank(modeName),
      mask,
    });
  }

  // Ce qui couvre toute la progression d'abord, le propre avant le harsh, puis
  // les gammes les plus courantes, puis celles qui touchent le plus de notes
  // d'accords.
  found.sort((a, b) => {
    if (a.covers !== b.covers) return a.covers ? -1 : 1;
    if (a.harsh !== b.harsh) return a.harsh ? 1 : -1;
    if (a.tier !== b.tier) return a.tier - b.tier;
    if (a.reached !== b.reached) return b.reached - a.reached;
    return a.mode.localeCompare(b.mode);
  });

  // Deux modes au meme jeu de notes (alias possible dans le pack) : seul le
  // mieux classe represente l'ensemble. La dedup vient apres le tri pour que
  // le nom retenu soit le plus courant, pas le premier itere.
  const seen = new Set();
  const deduped = found.filter((s) => {
    if (seen.has(s.mask)) return false;
    seen.add(s.mask);
    return true;
  });

  // Une progression courte (deux accords) est couverte par des dizaines de
  // gammes : sans place reservee, elles prennent toute la liste et les
  // pentatoniques, qui sont la premiere chose qu'un guitariste essaie,
  // disparaissent. Les internes gardent donc leurs sieges.
  const covering = deduped.filter((s) => s.covers);
  const insiders = deduped.filter((s) => !s.covers);
  const insideSeats = Math.min(insiders.length, INSIDE_SEATS);
  const coveringTake = Math.min(covering.length, MAX_SUGGESTIONS - insideSeats);
  const shortlist = [
    ...covering.slice(0, coveringTake),
    ...insiders.slice(0, MAX_SUGGESTIONS - coveringTake),
  ];

  return shortlist.map(({ mode: modeName, covers, harsh }) => ({
    root,
    mode: modeName,
    covers,
    harsh,
    label: formatScaleLabel(root, modeName),
  }));
};

export const nextImproScale = (options = [], current = null, dir = 1) => {
  const cycle = [null, ...options.map(({ root, mode }) => ({ root, mode }))];
  const found = current
    ? cycle.findIndex((s) => s && s.root === current.root && s.mode === current.mode)
    : 0;
  const index = found === -1 ? 0 : found;
  const step = dir < 0 ? -1 : 1;
  return cycle[(index + step + cycle.length) % cycle.length];
};

// Libelle d'une gamme tel qu'il s'affiche : fondamentale orthographiee dans la
// gamme (Eb et pas D#) suivie du nom courant du mode. Les alterations ne sont
// converties que sur la fondamentale : passer le nom du mode dans le formateur
// transformerait le n de "Ionian" en becarre.
export const formatScaleLabel = (root, mode) =>
  `${formatAccidentals(getDisplayRootNote(root, mode))} ${getModeDisplayLabel(mode)}`;

// Type de gamme choisi pendant un backing, reporte section par section. Les MIDI
// ne portent que la fondamentale de chaque section, le jugement se fait donc sur
// la gamme DECLAREE de la section, sans progression (tonalite entiere comme
// reference). Le type ne s'applique que s'il fait partie des gammes posables sur
// cette gamme ; sinon le manche revient sur la section et le choix reste en
// pause, pret a revenir sur une section qui l'accepte. On compare le mode seul :
// la fondamentale des options est normalisee (A# pour Bb), celle des marqueurs
// ne l'est pas. Pas de filtre de tonalite : c'est le backing qui pose la root.
// checked = false pour une gamme posee a la main par l'auteur (dialogue du
// player, palier de la carte) : la penta mineure sur du majeur ou le blues sur
// du mineur sont des choix de style, la regle diatonique ne les juge pas.
export const resolveBackingScale = ({ type, rootNote, mode, isModeUnlocked, checked = true }) => {
  const options = getCompatibleImproScales({
    rootNote,
    mode,
    progressionName: null,
    isModeUnlocked,
  });
  const fits = !!type && (!checked || type === mode || options.some((o) => o.mode === type));
  return {
    root: rootNote,
    mode: fits ? type : mode,
    paused: !!type && !fits,
    options,
  };
};
